import InfoCard from '@/components/ui/InfoCard';
import { ActionSheet } from '@/components/ui/action-sheet';
import { Button } from '@/components/ui/button';
import { ChangeEvent, useRef, useState } from 'react';
import { CheckCircle2, Headset } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CoachmarkTarget } from '@/components/ui/Coachmark';
import { createSupportRequest } from '../../api';
import { createSupportRequestPayload } from '../../model/createSupportRequestPayload';
import { getErrorMessage } from '@/shared/lib/getErrorMessage';
import { matchPath, useLocation } from 'react-router-dom';
import { onboardingTargetIds, useBlockingInterface } from '@/features/Onboarding';
import { SupportOperatorEntryProps } from './types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMutation } from '@tanstack/react-query';
import { useShipmentDetailsApi } from '@/features/Shipment';

const supportOperatorEmptyMessageError = 'Введите сообщение';
const supportOperatorRequestError = 'Не удалось отправить сообщение. Попробуйте ещё раз.';
const maxSupportOperatorMessageLength = 500;
const supportOperatorButtonLabel = 'Позвать оператора';
const supportOperatorSuccessDescription = 'Оператор получит ваш вопрос и свяжется с вами в рабочее время';
const supportOperatorSuccessTitle = 'Сообщение отправлено';
const supportOperatorSheetTitle = 'Написать оператору';

const SupportOperatorEntry = (props: SupportOperatorEntryProps) => {
  const { className } = props;

  const [clientRequestId, setClientRequestId] = useState(() => crypto.randomUUID());
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState('');

  useBlockingInterface('support-operator', isOpen);

  const isSubmittingRef = useRef(false);
  const location = useLocation();
  const shipmentRouteMatch = matchPath(
    {
      end: false,
      path: '/shipments/:shipmentId'
    },
    location.pathname
  );
  const shipmentId = shipmentRouteMatch?.params.shipmentId;
  const { data: dataGetShipmentById } = useShipmentDetailsApi(shipmentId, {
    enabled: isOpen && Boolean(shipmentId)
  });
  const shipmentNumber = dataGetShipmentById?.number?.trim();
  const { isPending: isPendingCreateSupportRequest, mutate: mutateCreateSupportRequest } = useMutation({
    mutationFn: createSupportRequest,
    mutationKey: ['createSupportRequest', location.pathname],
    onError: (error) => {
      setMessageError(getErrorMessage(error, supportOperatorRequestError));
    },
    onSettled: () => {
      isSubmittingRef.current = false;
    },
    onSuccess: () => {
      setIsSubmitted(true);
    }
  });

  const onChangeMessage = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setMessage(event.target.value.slice(0, maxSupportOperatorMessageLength));
    setMessageError('');
  };

  const onClickSubmit = (): void => {
    if (isSubmittingRef.current) return;

    if (message.trim().length === 0) {
      setMessageError(supportOperatorEmptyMessageError);
      return;
    }

    setMessageError('');
    isSubmittingRef.current = true;
    mutateCreateSupportRequest(
      createSupportRequestPayload({
        clientRequestId,
        message,
        pathname: location.pathname,
        shipmentId
      })
    );
  };

  const onClickSupportOperatorButton = (): void => {
    if (isSubmitted) {
      setClientRequestId(crypto.randomUUID());
    }

    setIsSubmitted(false);
    setMessage('');
    setMessageError('');
    setIsOpen(true);
  };

  const onOpenChangeActionSheet = (nextIsOpen: boolean): void => {
    setIsOpen(nextIsOpen);
  };

  return (
    <>
      <CoachmarkTarget targetId={onboardingTargetIds.supportOperator}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={cn(
                  'shrink-0 text-muted-foreground',
                  'hover:bg-muted hover:text-foreground',
                  '[[data-coachmark-target-active=true]_&]:text-foreground',
                  className
                )}
                aria-label={supportOperatorButtonLabel}
                onClick={onClickSupportOperatorButton}
                size="icon"
                type="button"
                variant="ghost"
              >
                <Headset className="size-5" aria-hidden="true" strokeWidth={1.75} />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="hidden min-[769px]:block" side="bottom">
              {supportOperatorButtonLabel}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CoachmarkTarget>
      <ActionSheet
        footer={
          isSubmitted ? null : (
            <Button
              className="w-full"
              disabled={isPendingCreateSupportRequest}
              loading={isPendingCreateSupportRequest}
              onClick={onClickSubmit}
              size="lg"
              type="button"
            >
              Отправить
            </Button>
          )
        }
        onOpenChange={onOpenChangeActionSheet}
        open={isOpen}
        title={supportOperatorSheetTitle}
        titleId="support-operator-title"
      >
        {isSubmitted ? (
          <div className={cn('flex items-center', shipmentNumber ? 'min-h-[317px]' : 'min-h-[293px]')}>
            <InfoCard
              description={supportOperatorSuccessDescription}
              icon={<CheckCircle2 className="size-6 text-muted-foreground" strokeWidth={1.4} />}
              title={supportOperatorSuccessTitle}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {shipmentNumber ? (
              <p className="font-sans text-xs leading-4 text-muted-foreground">Поставка № {shipmentNumber}</p>
            ) : null}
            <p className="font-sans text-sm font-semibold leading-5 text-muted-foreground">
              Оператор ответит в рабочее время
            </p>
            <textarea
              className={cn(
                'h-40 w-full resize-none px-4 py-2',
                'font-sans text-base leading-6 text-foreground placeholder:text-muted-foreground-soft',
                'rounded-2xl border bg-surface',
                'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent',
                messageError ? 'border-destructive' : 'border-border'
              )}
              aria-describedby={messageError ? 'support-operator-message-error' : undefined}
              aria-invalid={messageError ? 'true' : undefined}
              aria-label="Сообщение оператору"
              maxLength={maxSupportOperatorMessageLength}
              onChange={onChangeMessage}
              placeholder="Ваше сообщение..."
              value={message}
            />
            <div className="flex items-start justify-between gap-3 px-3">
              {messageError ? (
                <p className="font-sans text-xs leading-4 text-destructive" id="support-operator-message-error">
                  {messageError}
                </p>
              ) : (
                <span />
              )}
              <p className="shrink-0 font-sans text-xs leading-4 text-muted-foreground">
                {message.length}/{maxSupportOperatorMessageLength}
              </p>
            </div>
          </div>
        )}
      </ActionSheet>
    </>
  );
};

export default SupportOperatorEntry;